import React, { useRef, useState, useCallback, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useIntl } from 'react-intl'
import { Box, TablePagination, Typography } from '@mui/material'
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table'
import { MRT_Localization_ES } from 'material-react-table/locales/es'
import { tableStyles } from './table-global-styles'

// Utility function to calculate column pinning based on fixedColumns configuration
const calculateColumnPinning = (columns, fixedColumns) => {
  if (!fixedColumns || (!fixedColumns.left && !fixedColumns.right)) {
    return {}
  }

  const columnPinning = {}

  // Pin left columns
  if (fixedColumns.left && fixedColumns.left > 0) {
    const leftColumns = columns.slice(0, fixedColumns.left).map((col) => col.id || col.accessorKey)
    if (leftColumns.length > 0) {
      columnPinning.left = leftColumns
    }
  }

  // Pin right columns
  if (fixedColumns.right && fixedColumns.right > 0) {
    const rightColumns = columns.slice(-fixedColumns.right).map((col) => col.id || col.accessorKey)
    if (rightColumns.length > 0) {
      columnPinning.right = rightColumns
    }
  }

  return columnPinning
}

// Utility function to merge global styles with custom styles
const mergeStyles = (globalStyles, customStyles) => {
  if (!customStyles) return globalStyles
  return { ...globalStyles, ...customStyles }
}

/**
 * @typedef {object} TableUrlPaginationProps
 * @property {Array<object>} columns - Configuración de columnas para MaterialReactTable.
 * @property {Array<object>} [data=[]] - Datos de las filas de la tabla.
 * @property {number} [totalEntries=0] - Número total de entradas (para paginación).
 * @property {boolean} [isLoading=false] - Si es true, muestra el estado de carga.
 * @property {object} [options={}] - Opciones adicionales para la tabla (incluye fixedColumns).
 * @property {object} [search={}] - Objeto con parámetros de búsqueda actuales.
 * @property {function(event, object):void} [onRowClick] - Callback invocado al hacer click en una fila.
 * @property {function(Array<object>):void} [onSelectionChange] - Callback invocado al cambiar la selección de filas.
 * @property {object|function} [muiTableBodyRowProps] - Props adicionales para las filas del body.
 */

/**
 * Tabla avanzada con paginación, ordenación y gestión automática de URL.
 *
 * Tabla basada en MaterialReactTable que sincroniza automáticamente paginación, ordenación
 * y otros parámetros con la URL (query string). Incluye soporte para columnas fijadas,
 * selección de filas, estados de carga, localización en español, y estilos globales
 * combinables con estilos personalizados.
 *
 * @component
 * @example
 * const columns = [
 *   { accessorKey: 'name', header: 'Nombre' },
 *   { accessorKey: 'email', header: 'Email' }
 * ];
 * <TableUrlPagination
 *   columns={columns}
 *   data={rows}
 *   totalEntries={100}
 *   isLoading={false}
 *   onRowClick={(e, row) => console.log(row)}
 *   options={{ fixedColumns: { left: 1 } }}
 * />
 *
 * @param {TableUrlPaginationProps} props - Las props del componente TableUrlPagination.
 */
export const TableUrlPagination = ({
  columns,
  data = [],
  totalEntries = 0,
  isLoading = false,
  options = {},
  search = {},
  onRowClick,
  onSelectionChange,
  muiTableBodyRowProps,
}) => {
  const intl = useIntl()
  const navigate = useNavigate()
  const location = useLocation()
  const query = useRef(new URLSearchParams(location.search)).current

  const { fixedColumns, ...restOptions } = options

  const urlPage = parseInt(query.get('_pagina'), 10) || 1
  const urlPerPage = parseInt(query.get('_num_x_pagina'), 10) || 5

  const page = parseInt(search['_pagina'], 10) || urlPage
  const pageSize = parseInt(search['_num_x_pagina'], 10) || urlPerPage

  const initialOrder = query.get('_order')
  const initialOrderBy = query.get('_order_by')
  const [sorting, setSorting] = useState(
    initialOrder && initialOrderBy ? [{ id: initialOrderBy, desc: initialOrder === 'desc' }] : []
  )

  const [pagination, setPagination] = useState({
    pageIndex: page - 1,
    pageSize: pageSize,
  })

  // Update pagination state and URL when pagination changes
  const handlePaginationChange = useCallback(
    (updaterOrValue) => {
      const newPagination =
        typeof updaterOrValue === 'function' ? updaterOrValue(pagination) : updaterOrValue
      setPagination(newPagination)

      const currentQuery = new URLSearchParams(location.search)
      currentQuery.set('_pagina', (newPagination.pageIndex + 1).toString())
      currentQuery.set('_num_x_pagina', newPagination.pageSize.toString())
      navigate({ search: '?' + currentQuery.toString() }, { replace: true })
    },
    [navigate, location.search, pagination]
  )

  // Update sorting state and URL when sorting changes
  const handleSortingChange = useCallback(
    (updaterOrValue) => {
      const newSorting =
        typeof updaterOrValue === 'function' ? updaterOrValue(sorting) : updaterOrValue
      setSorting(newSorting)

      const currentQuery = new URLSearchParams(location.search)
      currentQuery.set('_pagina', '1')
      if (newSorting.length > 0) {
        currentQuery.set('_order', newSorting[0].desc ? 'desc' : 'asc')
        currentQuery.set('_order_by', newSorting[0].id)
      } else {
        currentQuery.delete('_order')
        currentQuery.delete('_order_by')
      }
      navigate({ search: '?' + currentQuery.toString() }, { replace: true })
    },
    [navigate, location.search, sorting]
  )

  // Legacy handlers for TablePagination component
  const handlePageChange = useCallback(
    (_evt, newPage0) => {
      handlePaginationChange({ pageIndex: newPage0, pageSize: pagination.pageSize })
    },
    [handlePaginationChange, pagination.pageSize]
  )

  const handleRowsPerPageChange = useCallback(
    (evt) => {
      const newSize = parseInt(evt.target.value, 10)
      handlePaginationChange({ pageIndex: 0, pageSize: newSize })
    },
    [handlePaginationChange]
  )

  // Row selection state
  const [rowSelection, setRowSelection] = useState({})

  const handleRowClick = useCallback(
    (row, event) => {
      if (onRowClick) {
        onRowClick(event, row.original)
      }
    },
    [onRowClick]
  )

  const handleRowSelectionChange = useCallback(
    (updaterOrValue) => {
      const newSelection =
        typeof updaterOrValue === 'function' ? updaterOrValue(rowSelection) : updaterOrValue
      setRowSelection(newSelection)
      if (onSelectionChange) {
        const selectedRowIds = Object.keys(newSelection).filter((key) => newSelection[key])
        const selectedRows = data.filter((row, index) => selectedRowIds.includes(index.toString()))
        onSelectionChange(selectedRows)
      }
    },
    [onSelectionChange, data, rowSelection]
  )

  // Process columns to apply global styles while preserving custom styles
  const processedColumns = useMemo(() => {
    return columns.map((column) => {
      const processedColumn = { ...column }

      if (processedColumn.muiTableHeadCellProps) {
        const existingHeaderSx = processedColumn.muiTableHeadCellProps.sx || {}
        processedColumn.muiTableHeadCellProps = {
          ...processedColumn.muiTableHeadCellProps,
          sx: mergeStyles(tableStyles.header, existingHeaderSx),
        }
      } else {
        processedColumn.muiTableHeadCellProps = {
          sx: tableStyles.header,
        }
      }

      if (processedColumn.muiTableBodyCellProps) {
        const existingCellSx = processedColumn.muiTableBodyCellProps.sx || {}
        processedColumn.muiTableBodyCellProps = {
          ...processedColumn.muiTableBodyCellProps,
          sx: mergeStyles(tableStyles.cell, existingCellSx),
        }
      } else {
        processedColumn.muiTableBodyCellProps = {
          sx: tableStyles.cell,
        }
      }

      return processedColumn
    })
  }, [columns])

  // Material React Table configuration
  const table = useMaterialReactTable({
    columns: processedColumns,
    data: data,
    manualPagination: true,
    manualSorting: true,
    rowCount: totalEntries,
    enableColumnPinning: true,
    localization: MRT_Localization_ES,
    state: {
      pagination,
      sorting,
      rowSelection,
      isLoading,
    },
    initialState: {
      columnPinning: calculateColumnPinning(columns, fixedColumns),
    },
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        '& .MuiTableHead-root .MuiTableCell-head': {
          backgroundColor: 'rgba(0, 65, 101, 0.1)',
          boxSizing: 'border-box',
        },
        '& .MuiTableHead-root .MuiTableCell-head[data-pinned="true"]': {
          backgroundColor: 'white',
          boxSizing: 'border-box',
        },
        '& .MuiTableCell-head[data-pinned="true"]:before': {
          backgroundColor: 'rgba(0, 65, 101, 0.1)',
        },
        '& .MuiTableCell-root': {
          boxSizing: 'border-box',
        },
        '& .MuiTableBody-root .MuiTableCell-root': {
          boxSizing: 'border-box',
        },
        '& .MuiTableCell-root[data-select="true"]': {
          padding: '8px 16px',
          height: '40px',
          display: 'table-cell',
          verticalAlign: 'middle',
          textAlign: 'center',
        },
        '& .mrt-row-select .MuiCheckbox-root': {
          padding: '4px',
          margin: 0,
        },
      },
    },
    muiTableHeadProps: {
      sx: {
        '& .MuiTableCell-head': {
          backgroundColor: 'rgba(0, 65, 101, 0.1)',
          fontSize: 14,
          color: '#1a171b',
          border: 'none',
          boxSizing: 'border-box',
        },
        '& .MuiTableCell-head[data-select="true"]': {
          padding: '8px 16px',
          height: '56px',
          display: 'table-cell',
          verticalAlign: 'middle',
          textAlign: 'center',
        },
      },
    },
    muiSelectCheckboxProps: {
      sx: {
        '& .MuiTableCell-head': {
          backgroundColor: 'rgba(0, 65, 101, 0.1)',
          boxSizing: 'border-box',
          padding: '8px 16px',
          height: '56px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          verticalAlign: 'middle',
        },
        '& .MuiCheckbox-root': {
          padding: '4px',
          margin: 0,
        },
      },
    },
    muiSelectAllCheckboxProps: {
      sx: {
        padding: '4px',
        margin: 0,
      },
    },
    onPaginationChange: handlePaginationChange,
    onSortingChange: handleSortingChange,
    onRowSelectionChange: handleRowSelectionChange,
    enableRowSelection: options.selection || false,
    enableSorting: true,
    enablePagination: false,
    enableBottomToolbar: false,
    enableTopToolbar: false,
    enableColumnActions: false,
    enableColumnFilters: false,
    enableGlobalFilter: false,
    enableFullScreenToggle: false,
    enableDensityToggle: false,

    muiTableBodyRowProps: ({ row }) => {
      const externalProps =
        typeof muiTableBodyRowProps === 'function' ? muiTableBodyRowProps({ row }) : {}

      const internalProps = {
        onClick: onRowClick ? (event) => handleRowClick(row, event) : undefined,
        sx: {
          cursor: onRowClick ? 'pointer' : 'default',
          '&:hover': onRowClick
            ? {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              }
            : {},
        },
      }

      return {
        ...externalProps,
        ...internalProps,
        sx: {
          ...externalProps.sx,
          ...internalProps.sx,
        },
      }
    },

    renderEmptyRowsFallback: () => (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '200px',
          width: '100%',
          padding: 2,
        }}
      >
        <Typography
          variant="body1"
          sx={{
            color: '#63666a',
            fontSize: '14px',
            textAlign: 'center',
          }}
        >
          {intl.formatMessage({ id: 'table.noResults' })}
        </Typography>
      </Box>
    ),
    ...restOptions,
  })

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      <Box sx={{ flexGrow: 1, position: 'relative', height: '100%' }}>
        <MaterialReactTable table={table} />
      </Box>

      <TablePagination
        sx={{ backgroundColor: '#fff', border: '1px solid #e0e0e0' }}
        component="div"
        count={totalEntries}
        page={pagination.pageIndex}
        rowsPerPage={pagination.pageSize}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        rowsPerPageOptions={[
          { value: 5, label: '5' },
          { value: 10, label: '10' },
          { value: 20, label: '20' },
        ]}
        labelRowsPerPage="Registros"
        showFirstButton
        showLastButton
      />
    </Box>
  )
}
